import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ViewStyle } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';

interface ToggleOption {
  key: string;
  label: string;
}

interface ToggleBarProps {
  options: ToggleOption[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Optional max width constraint (useful for larger screens) */
  maxWidth?: number;
  /** Optional custom container style */
  containerStyle?: ViewStyle;
}

/**
 * Reusable toggle bar component for switching between options
 * Used in MarketplaceProfileScreen (Active/Sold) and StepPriceCondition (Set price/Free item)
 */
export const ToggleBar: React.FC<ToggleBarProps> = ({
  options,
  activeKey,
  onSelect,
  maxWidth,
  containerStyle,
}) => {
  return (
    <View 
      style={[
        styles.container,
        maxWidth ? { maxWidth } : undefined,
        containerStyle,
      ]}
    >
      {options.map((option) => {
        const isActive = option.key === activeKey;
        return (
          <TouchableOpacity
            key={option.key}
            style={[styles.option, isActive && styles.optionActive]}
            onPress={() => onSelect(option.key)}
            activeOpacity={0.7}
          >
            <Text style={[styles.optionText, isActive && styles.optionTextActive]}>
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: MARKETPLACE_COLORS.toggleBackground,
    borderRadius: 8,
    padding: 2,
  },
  option: {
    flex: 1,
    height: 30,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionActive: {
    backgroundColor: MARKETPLACE_COLORS.toggleActiveBackground,
  },
  optionText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  optionTextActive: {
    color: MARKETPLACE_COLORS.textPrimary,
  },
});
